// SPDX-License-Identifier: Apache-2.0

// caret-mirror — get the (left, top) viewport coordinates of the caret inside
// a `<textarea>`. The textarea has no native API for this, so we use the
// "hidden mirror div" trick popularized by `textarea-caret-position`:
//
//   1. Build a fixed-position, off-screen `<div>` once and cache it.
//   2. Copy every relevant computed style from the textarea onto the div
//      (font, padding, border, line-height, white-space, word-wrap, …) so
//      the div word-wraps identically.
//   3. Set the div's text content to `value.slice(0, caret)` followed by a
//      zero-width `<span>` marker, then the rest. The span's bounding rect
//      is the caret's pixel position inside the mirror.
//   4. Translate that into viewport coordinates by adding the textarea's
//      own bounding rect, minus its scroll offsets.
//
// This is the same approach used by every modern textarea-based autocomplete
// (Slack, Linear, GitHub's @-mention popover). Reasonably accurate to ±1px
// across browsers; the rendered popover sits a few px below the caret line
// height so the user's text isn't visually obscured.

export interface CaretXY {
  /** viewport x, ready to drop into `style.left`. */
  left: number;
  /** viewport y BELOW the caret line, ready to drop into `style.top`. */
  top: number;
}

const COPIED_PROPERTIES: Array<keyof CSSStyleDeclaration> = [
  "boxSizing",
  "width",
  "height",
  "overflowX",
  "overflowY",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderStyle",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "fontStyle",
  "fontVariant",
  "fontWeight",
  "fontStretch",
  "fontSize",
  "fontSizeAdjust",
  "lineHeight",
  "fontFamily",
  "textAlign",
  "textTransform",
  "textIndent",
  "textDecoration",
  "letterSpacing",
  "wordSpacing",
  "tabSize",
  "MozTabSize" as keyof CSSStyleDeclaration,
];

let mirror: HTMLDivElement | null = null;

function getMirror(): HTMLDivElement {
  if (mirror) return mirror;
  const div = document.createElement("div");
  div.setAttribute("data-mv-caret-mirror", "");
  const s = div.style;
  s.position = "absolute";
  s.visibility = "hidden";
  s.whiteSpace = "pre-wrap";
  s.wordWrap = "break-word";
  s.top = "0";
  s.left = "-9999px";
  s.overflow = "hidden";
  document.body.appendChild(div);
  mirror = div;
  return div;
}

/**
 * Compute the caret's viewport-relative (left, top) inside `textarea` for the
 * given caret index. `top` is the BOTTOM of the caret's line (so popovers can
 * sit just under it).
 */
export function computeCaretXY(
  textarea: HTMLTextAreaElement,
  caret: number,
): CaretXY {
  const div = getMirror();
  const style = window.getComputedStyle(textarea);

  for (const prop of COPIED_PROPERTIES) {
    try {
      // CSSStyleDeclaration is index-by-string; cast to any for the indexed write.
      (div.style as unknown as Record<string, string>)[prop as string] =
        style.getPropertyValue(prop as string) ||
        (style as unknown as Record<string, string>)[prop as string] ||
        "";
    } catch {
      /* some properties throw on read in old jsdom; ignore */
    }
  }

  // Mirror behaves like a div; the textarea behaves like a div under
  // white-space:pre-wrap, but the mirror needs a fixed width so word-wrap
  // matches. The width copy above handles that.
  const before = textarea.value.slice(0, caret);
  const after = textarea.value.slice(caret) || "."; // trailing char prevents
  // collapse of the marker rect on lines that end at the caret.

  div.textContent = before;
  const marker = document.createElement("span");
  marker.textContent = "​"; // zero-width space — has zero advance, but
  // contributes a layout box from which we can read coords.
  div.appendChild(marker);
  const tail = document.createTextNode(after);
  div.appendChild(tail);

  const taRect = textarea.getBoundingClientRect();
  const markerRect = marker.getBoundingClientRect();
  const divRect = div.getBoundingClientRect();

  // Caret position in viewport space:
  //   left = textarea.left + (marker.x - mirror.x) - textarea.scrollLeft
  //   top  = textarea.top  + (marker.y - mirror.y) - textarea.scrollTop
  //          + line-height (so we sit BELOW the caret, not on top of it)
  const lineHeight =
    parseFloat(style.lineHeight) ||
    parseFloat(style.fontSize) * 1.2 ||
    16;

  const left = taRect.left + (markerRect.left - divRect.left) - textarea.scrollLeft;
  const top =
    taRect.top +
    (markerRect.top - divRect.top) -
    textarea.scrollTop +
    lineHeight;

  // Cleanup — leaving stale text in the cached mirror is fine, but stripping
  // children avoids the next call appending to a giant child list.
  div.textContent = "";

  return { left, top };
}
